import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GiftButton } from "@/components/GiftSession";

const todayStr = () => new Date().toISOString().slice(0, 10);
const maxStr = () => new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

export const BookingWidget = ({ practitioner }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [serviceId, setServiceId] = useState(practitioner.services?.[0]?.id || "");
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setTime("");
    api.get(`/practitioners/${practitioner.id}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date, practitioner.id]);

  const service = practitioner.services?.find((s) => s.id === serviceId);
  const isOwn = user && user.id === practitioner.user_id;

  const book = async () => {
    if (!user) {
      toast.info("Sign in to book your session");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        practitioner_id: practitioner.id, service_id: serviceId, date, time,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="silk-card rounded-lg p-6" data-testid="booking-widget">
      <h2 className="font-display text-lg md:text-2xl text-foreground mb-5 flex items-center gap-2">
        <CalendarDays size={18} className="text-primary" /> Book a session
      </h2>
      <div className="space-y-4">
        <Select value={serviceId} onValueChange={setServiceId}>
          <SelectTrigger data-testid="booking-service-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
          <SelectContent>
            {practitioner.services?.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name} — ${s.price} CAD</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="date" data-testid="booking-date-input" value={date} min={todayStr()} max={maxStr()}
          onChange={(e) => setDate(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground" />
        <div data-testid="booking-slots">
          {loadingSlots ? (
            <p className="text-sm text-muted-foreground">Loading times…</p>
          ) : slots.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="booking-no-slots">No times available this day — try another date.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((s) => (
                <button key={s} type="button" data-testid={`booking-slot-${s.replace(":", "")}`}
                  onClick={() => setTime(s)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${time === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        {service && time && (
          <div className="text-sm text-muted-foreground border-t border-border pt-4" data-testid="booking-summary">
            {service.name} · {date} at {time} · <span className="text-primary">${service.price} CAD</span>
          </div>
        )}
        <Button data-testid="booking-pay-btn" disabled={!serviceId || !time || busy || isOwn} onClick={book}
          className="w-full h-11 rounded-full">
          {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : null}
          {isOwn ? "This is your listing" : busy ? "Redirecting to payment…" : "Book & pay securely"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">Secure payment via Stripe · CAD</p>
        <GiftButton practitioner={practitioner} />
      </div>
    </div>
  );
};
